import { CheckCircleOutlined } from '@ant-design/icons';
import IconSquare from 'assets/logos/icon-square.svg?react';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Empty, EmptyDescription, EmptyHeader } from 'components/ui/empty';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PostMetadataDTO } from 'services/api/models/PostDTO';
import { routes } from 'services/RouteService';
import onArticleSelected, { PosthogOnArticleOpenExtras } from 'utils/ContentUtils';

export default function PostsList({
  posts,
  limitPosts,
  posthogOnArticleOpenExtras,
  extraReadMarkers,
  getLabel,
  testIdPrefix = 'postinlist',
}: {
  posts: PostMetadataDTO[];
  limitPosts?: number;
  posthogOnArticleOpenExtras?: PosthogOnArticleOpenExtras;
  /** When true, show a "Lu" Tag with check mark for read posts */
  extraReadMarkers?: boolean;
  /** A small line under the title, e.g. the theme a post belongs to */
  getLabel?: (post: PostMetadataDTO) => string | undefined;
  /** Several lists on one page (the categorised Infos page) need distinct test ids */
  testIdPrefix?: string;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const displayedPosts = limitPosts ? posts.slice(0, limitPosts) : posts;

  const displayShowMore = limitPosts && posts.length > limitPosts;

  return (
    <div className="flex w-full flex-col gap-3">
      {posts.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyDescription>{t('infoPage.noPosts')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        displayedPosts?.map((post, index) => (
          <div
            // The radius matches the search bar, so the two read as the same shape language.
            className="bg-card flex h-16 w-full items-center gap-3 overflow-hidden rounded-lg pr-4 shadow-xs"
            key={post.id}
            data-testid={`${testIdPrefix}-${index}`}
            onClick={() => onArticleSelected(post, index, navigate, dispatch, posthogOnArticleOpenExtras)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onArticleSelected(post, index, navigate, dispatch, posthogOnArticleOpenExtras);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="size-16 shrink-0 overflow-hidden">
              {post.cover_image_url ? (
                <img src={post.cover_image_url} alt={post.title} className="size-full object-cover opacity-70" />
              ) : (
                <div className="size-full object-cover opacity-70">
                  <IconSquare className="size-full object-cover opacity-70" />
                </div>
              )}
            </div>
            <div className={cn('flex grow items-center gap-2', post.read && 'text-subtle-foreground')}>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {/* With a label underneath, two lines of title would make the row too tall */}
                <h5 className={cn('text-base font-normal', getLabel?.(post) ? 'line-clamp-1' : 'line-clamp-2')}>
                  {post.title}
                </h5>
                {getLabel?.(post) && <span className="text-muted-foreground text-xs">{getLabel(post)}</span>}
              </div>
              {post.is_new && !post.read && <Badge variant="success">{t('infoPage.new')}</Badge>}
              {extraReadMarkers && post.read && (
                <Badge variant="success">
                  <CheckCircleOutlined />
                  {t('infoPage.read')}
                </Badge>
              )}
            </div>
          </div>
        ))
      )}
      {displayShowMore && (
        <Button size="xl" className="w-full" onClick={() => navigate(routes.info.route)}>
          {t('home.info.showMore')}
        </Button>
      )}
    </div>
  );
}
